'use client';

import SearchField from '@/components/SearchForm/SearchField';
import SelectedFilters from '@/components/SearchForm/SelectedFilters';
import useSearchForm, { UseSearchFormProps } from '@/components/SearchForm/useSearchForm';
import { cn } from '@/helpers/common';
import React from 'react';

const SearchFormJC: React.FC<UseSearchFormProps> = ({
  fromHomePage,
  q,
  l,
  baseUrl,
  queryParameters,
  scrollElementId,
  locations,
  occupations,
  industries,
  seniorities,
}) => {
  const {
    keyword,
    location,
    locationSuggestions,
    keywordSuggestions,
    isKeywordsSuggestionsOpened,
    isLocationsSuggestionsOpened,
    setIsKeywordsSuggestionsOpened,
    setIsLocationsSuggestionsOpened,
    onSubmit,
    onFormKeyDown,
    onKeywordChange,
    onLocationChange,
    cancelKeywordSearch,
    cancelLocationSearch,
    onKeywordSuggestionsClick,
    onLocationSuggestionsClick,
    multipleLocations,
    getRemoveFilterHref,
  } = useSearchForm({
    fromHomePage,
    q,
    l,
    baseUrl,
    queryParameters,
    scrollElementId,
    locations,
    occupations,
    industries,
    seniorities,
  });

  return (
    <div className="p-5 rounded-xl bg-white jc-shadow-form">
      <form
        className={'flex flex-col gap-3 lg:flex-row lg:justify-center lg:items-center'}
        onSubmit={onSubmit}
        onKeyDown={onFormKeyDown}
      >
        <SearchField
          key="q-input"
          ariaLabel="Search jobs"
          icon=""
          showLeftIcon={false}
          name="q"
          id="q"
          placeholder={'Job title, skills, or company'}
          maxLength={200}
          minLength={1}
          value={keyword}
          onChange={onKeywordChange}
          cancelSearch={cancelKeywordSearch}
          suggestions={keywordSuggestions}
          onSuggestionsClick={onKeywordSuggestionsClick}
          isSuggestionsOpened={isKeywordsSuggestionsOpened}
          setIsSuggestionsOpened={setIsKeywordsSuggestionsOpened}
          className="w-full flex-col lg:flex-[1_1_590px] lg:max-w-147.5"
          classNames={{
            input: `py-3.5 px-3 rounded-lg! lg:w-full bg-jc-white-lilac max-md:text-base md:text-sm default-transition outline-transparent border border-jc-botticelli placeholder-jc-gray-chateau focus-visible:border-jc-firefly focus-visible:bg-white focus-visible:outline-jc-firefly focus-visible:outline-1`,
            suggestions: 'rounded-md bg-white jc-shadow1',
            suggestionElement: 'hover:bg-jc-white-lilac text-sm py-2 text-jc-firefly',
            suggestionElementActive: 'bg-jc-white-lilac',
          }}
          autoFocus={!keyword}
        />
        <SearchField
          key="l-input"
          ariaLabel="Location"
          icon=""
          showLeftIcon={false}
          name="l"
          id="l"
          placeholder={'City, or ‘remote’'}
          maxLength={100}
          minLength={1}
          value={location}
          onChange={onLocationChange}
          cancelSearch={cancelLocationSearch}
          suggestions={locationSuggestions}
          onSuggestionsClick={onLocationSuggestionsClick}
          isSuggestionsOpened={isLocationsSuggestionsOpened}
          setIsSuggestionsOpened={setIsLocationsSuggestionsOpened}
          className="w-full flex-col lg:flex-[1_1_310px] lg:max-w-77.5"
          classNames={{
            input: `py-3.5 px-3 rounded-lg! lg:w-full bg-jc-white-lilac max-md:text-base md:text-sm default-transition outline-transparent border border-jc-botticelli placeholder-jc-gray-chateau focus-visible:border-jc-firefly focus-visible:bg-white focus-visible:outline-jc-firefly focus-visible:outline-1`,
            suggestions: 'rounded-md bg-white jc-shadow1',
            suggestionElement: 'hover:bg-jc-white-lilac text-sm py-2 text-jc-firefly',
            suggestionElementActive: 'bg-jc-white-lilac',
          }}
        />
        <button
          className={cn(
            'h-12.5 default-disabled default-transition second-font tracking-wider shrink-0',
            'text-jc-firefly rounded-md px-5 py-3 bg-jc-my-sin font-semibold text-center hover:bg-jc-tangerine',
          )}
          type="submit"
        >
          SEARCH JOBS
        </button>
      </form>
      <SelectedFilters
        locations={multipleLocations}
        occupations={occupations}
        industries={industries}
        seniorities={seniorities}
        getRemoveHref={getRemoveFilterHref}
        classNames={{
          label: 'text-jc-gray-chateau font-semibold',
          tag: 'text-jc-firefly bg-jc-white-lilac border border-jc-botticelli rounded-md',
          removeIcon: 'text-jc-gray-chateau hover:text-jc-firefly',
        }}
      />
    </div>
  );
};

export default SearchFormJC;
